// src/UserServices.js
import React, { useState, useEffect, useRef } from 'react';
import './UserServices.css';
import './ServiceCard.css';
import { apiRequest } from './utils';
import { resizeImage } from './ResizeImage';
import translations from './lang';

const UserServices = () => {
  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [expandedServiceId, setExpandedServiceId] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    street_address: '',
    address: '',
    city: '',
    country: '',
    available: true,
    category: '',
    SMS_notification: false,
    Email_notification: true,
  });

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState('');

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const categoryRef = useRef(null);

  useEffect(() => {
    fetchUserServices();
    fetchCategories();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formData.country) {
      fetchCities(formData.country);
    } else {
      setCities([]);
    }
  }, [language, formData.country]);

  useEffect(() => {
    if (showFormModal) setTimeout(() => nameRef.current?.focus(), 0);
  }, [showFormModal]);

  const fetchUserServices = () => {
    apiRequest(`${process.env.REACT_APP_API}/user-services/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then(r => r.json())
      .then(d => setServices(Array.isArray(d?.data) ? d.data : []))
      .catch(err => console.error('Error fetching services:', err));
  };

  const fetchCategories = () => {
    apiRequest(`${process.env.REACT_APP_API}/service-categories/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then(r => r.json())
      .then(d => setCategories(Array.isArray(d?.data) ? d.data : []))
      .catch(err => console.error('Error fetching categories:', err));
  };

  const fetchCountries = () => {
    apiRequest(`${process.env.REACT_APP_API}/countries/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
      },
    })
      .then(r => r.json())
      .then(d => setCountries(Array.isArray(d?.data) ? d.data : []))
      .catch(err => console.error('Error fetching countries:', err));
  };

  const getCountryLabel = (countryObjOrId) => {
    if (!countryObjOrId) return '';

    const resolveLabel = (country) => {
      if (!country) return '';

      if (language === 'hu') {
        return country.name_hu || country.name_ro || country.name || '';
      }

      if (language === 'ro') {
        return country.name_ro || country.name_hu || country.name || '';
      }

      return country.name || country.name_hu || country.name_ro || '';
    };

    if (typeof countryObjOrId === 'object') {
      return resolveLabel(countryObjOrId);
    }

    const country = countries.find(c => String(c.id) === String(countryObjOrId));
    return resolveLabel(country);
  };

  const getCityLabel = (cityObjOrId) => {
    if (!cityObjOrId) return '';

    const resolveLabel = (city) => {
      if (!city) return '';

      if (language === 'hu') {
        return city.name_hu || city.name_en || city.name_ro || '';
      }

      if (language === 'ro') {
        return city.name_ro || city.name_en || city.name_hu || '';
      }

      return city.name_en || city.name_hu || city.name_ro || '';
    };

    if (typeof cityObjOrId === 'object') {
      return resolveLabel(cityObjOrId);
    }

    const city = cities.find(c => String(c.id) === String(cityObjOrId));
    return resolveLabel(city);
  };

  const fetchCities = (countryId = '') => {
    if (!countryId) {
      setCities([]);
      return;
    }

    apiRequest(`${process.env.REACT_APP_API}/cities/?country=${countryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then(r => r.json())
      .then(d => {
        const rows = Array.isArray(d?.data) ? d.data : [];
        setCities(rows);
      })
      .catch(err => console.error('Error fetching cities:', err));
  };

  const buildAddress = (street, cityId, countryId) => {
    const cityName = getCityLabel(cityId);
    const countryName = getCountryLabel(countryId);
    return [street, cityName, countryName].filter(Boolean).join(', ');
  };

  const handleImageUpload = async (event, serviceId) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const resizedBlob = await resizeImage(file, 300, 'service');
      const fd = new FormData();
      fd.append('service_card_picture', resizedBlob, file.name);

      apiRequest(`${process.env.REACT_APP_API}/api/upload-service-card-picture/${serviceId}/`, {
        method: 'POST',
        body: fd,
        headers: {
          'Accept-Language': language,
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
        .then(r => r.json())
        .then(d => d.success ? fetchUserServices() : alert(t.image_upload_error))
        .catch(err => console.error('Error uploading image:', err));
    } catch (err) {
      console.error('Image resize error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'country') {
        updated.city = '';
      }

      updated.address = buildAddress(
        updated.street_address,
        updated.city,
        updated.country
      );

      return updated;
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const method = formData.id ? 'PUT' : 'POST';
    const url = `${process.env.REACT_APP_API}/user-services/${formData.id ? `${formData.id}/` : ''}`;

    const payload = {
      ...formData,
      city: formData.city || null,
      country: formData.country || null,
      category: formData.category || null,
    };

    apiRequest(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(payload),
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        alert(formData.id ? t.update_success : t.create_success);
        fetchUserServices();
        closeFormModal();
      })
      .catch(err => alert((t.general_error || 'Hiba: ') + err.message));
  };

  const openCreateModal = () => {
    setCities([]);
    setFormData({
      id: null,
      name: '',
      description: '',
      street_address: '',
      address: '',
      city: '',
      country: '',
      available: true,
      category: '',
      SMS_notification: false,
      Email_notification: true,
    });
    setShowFormModal(true);
  };

  const handleEdit = (service) => {
    setFormData({
      id: service.id,
      name: service.name || '',
      description: service.description || '',
      street_address: service.street_address || '',
      address: buildAddress(service.street_address, service.city, service.country),
      city: service.city || '',
      country: service.country || '',
      available: Boolean(service.available),
      category: service.category || '',
      SMS_notification: Boolean(service.SMS_notification),
      Email_notification: Boolean(service.Email_notification),
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => setShowFormModal(false);

  const handleDelete = (id) => {
    if (!window.confirm(t.confirm_delete_service || 'Biztosan törölni szeretné ezt a szolgáltatást?')) return;

    apiRequest(`${process.env.REACT_APP_API}/user-services/${id}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then(r => r.ok ? fetchUserServices() : r.json().then(d => { throw new Error(d?.error || 'Delete failed'); }))
      .catch(err => alert((t.delete_service_error || 'Törlési hiba: ') + err.message));
  };

  const toggleDetails = (id) => setExpandedServiceId(expandedServiceId === id ? null : id);

  const openMapModal = (addr) => {
    if (!addr) return;
    setMapAddress(addr);
    setMapModalOpen(true);
  };

  const getFullAddress = (address, cityId, countryId) => {
    const cityName = getCityLabel(cityId);
    const countryName = getCountryLabel(countryId);
    return [address, cityName, countryName].filter(Boolean).join(', ');
  };

  return (
    <div className="services-container">
      <h3>{t.existing_services}</h3>

      <div className="service-card-grid">
        {services.map(service => (
          <div key={service.id} className={`service-card ${expandedServiceId === service.id ? 'expanded' : ''}`}>
            <div
              className="service-card-background"
              style={{
                backgroundImage: service.service_card_picture
                  ? `url(data:image/jpeg;base64,${service.service_card_picture})`
                  : `url(${process.env.REACT_APP_API}/media/profile_pictures/default.jpg)`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                height: '400px',
                maxHeight: '400px',
                maxWidth: '400px',
                borderRadius: '7px',
                marginBottom: '7px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="service-card-content">
                <h4>{service.name}</h4>
              </div>

              <div className="service-card-button-image">
                <label className="service-card-custom-file-upload">
                  <img
                    src={`${process.env.REACT_APP_API}${service.user_data.upload_picture || '/media/profile_pictures/default.jpg'}`}
                    alt="Profile"
                  />
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, service.id)} />
                </label>
              </div>
            </div>

            {expandedServiceId === service.id && (
              <div className="service-card-details">
                <p>{service.description}</p>

                {service.address && (
                  <div>
                    <p>
                      {t.address || 'Cím'}:{' '}
                      <button
                        type="button"
                        onClick={() => openMapModal(getFullAddress(service.street_address, service.city, service.country))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0ea5e9',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                        }}
                        aria-label="Cím megnyitása térképen"
                      >
                        {service.address}
                      </button>
                    </p>
                  </div>
                )}

                <p>{t.category_label}: {service.category_name}</p>
                <p>{t.available_label}: {service.available ? t.yes : t.no}</p>
                <p>{t.sms_notification}: {service.SMS_notification ? t.yes : t.no}</p>
                <p>{t.email_notification}: {service.Email_notification ? t.yes : t.no}</p>

                <div className="service-card-packages">
                  <p><strong>{t.packages}:</strong></p>
                  {service.packages.length > 0 ? (
                    service.packages.map(pkg => (
                      <div key={pkg.id} className="package-details">
                        <p><strong>{t.name}:</strong> {pkg.name}</p>
                        <p><strong>{t.description}:</strong> {pkg.description}</p>
                        <p><strong>{t.price}:</strong> {pkg.price} RON</p>
                        <p><strong>{t.duration}:</strong> {parseInt(pkg.duration)} {t.hours} {Math.round((pkg.duration % 1) * 60)} {t.minutes}</p>
                      </div>
                    ))
                  ) : <p>{t.no_package_available}</p>}
                </div>
              </div>
            )}

            <div className="service-card-button-container">
              <button onClick={() => toggleDetails(service.id)}>
                {expandedServiceId === service.id ? t.close : t.details}
              </button>
              <button onClick={() => handleEdit(service)}>{t.edit}</button>
              <button onClick={() => handleDelete(service.id)}>{t.delete}</button>
            </div>
          </div>
        ))}

        <div className="service-card empty-card" onClick={openCreateModal}>
          <div className="service-card-content">
            <h4>{t.create_service}</h4>
            <p>{t.add_package_desc}</p>
          </div>
        </div>
      </div>

      {/* Form modal */}
      {showFormModal && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-content">
            <h4>{formData.id ? t.edit : t.new_service}</h4>

            <label>{t.name}:
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                ref={nameRef}
                required
              />
            </label>

            <label>{t.description}:
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                ref={descriptionRef}
              />
            </label>

            <label>{t.country || 'Ország'}:
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
              >
                <option value="">{t.choose_country || 'Válasszon országot'}</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{getCountryLabel(c)}</option>
                ))}
              </select>
            </label>

            <label>{t.city || 'Város'}:
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!formData.country}
              >
                <option value="">
                  {formData.country
                    ? (t.select_city || t.choose_city || 'Válasszon várost')
                    : (t.choose_country_first || 'Előbb válasszon országot')}
                </option>
                {cities.map(city => (
                  <option key={city.id} value={city.id}>
                    {getCityLabel(city)}
                  </option>
                ))}
              </select>
            </label>

            <label>{t.street_address || 'Utca, házszám, emelet, apartman'}:
              <input
                type="text"
                name="street_address"
                value={formData.street_address}
                onChange={handleInputChange}
                placeholder={t.street_address_placeholder || 'Pl.: Fő utca 12, 3. emelet, 4. lakás'}
              />
            </label>

            <label>{t.address || 'Cím'}:
              <input
                type="text"
                name="address"
                value={formData.address}
                readOnly
                placeholder={t.address_placeholder || 'Az utca, város és ország kombinációja'}
              />
            </label>

            {(formData.address || formData.city || formData.country) && (
              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{t.address || 'Cím'}:</span>
                </div>
                <div
                  className="map-mini"
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    height: 220,
                    background: '#f1f5f9',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)'
                  }}
                >
                  <iframe
                    title="Google Maps preview"
                    width="100%"
                    height="100%"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(getFullAddress(formData.street_address, formData.city, formData.country))}&hl=${encodeURIComponent(language)}&z=15&output=embed`}
                    loading="lazy"
                    style={{ border: 0, width: '100%', height: '100%' }}
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getFullAddress(formData.street_address, formData.city, formData.country))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.open_in_maps || 'Megnyitás a Google Térképen'}
                  </a>
                </div>
              </div>
            )}

            <label>{t.category}:
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                ref={categoryRef}
                required
              >
                <option value="">{t.choose_category}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>

            <label>
              <input
                type="checkbox"
                name="available"
                checked={formData.available}
                onChange={handleInputChange}
              /> {t.available}
            </label>

            <label>
              <input
                type="checkbox"
                name="SMS_notification"
                checked={formData.SMS_notification}
                onChange={handleInputChange}
              /> {t.sms_notification}
            </label>

            <label>
              <input
                type="checkbox"
                name="Email_notification"
                checked={formData.Email_notification}
                onChange={handleInputChange}
              /> {t.email_notification}
            </label>

            <div style={{ marginTop: 6 }}>
              <button onClick={handleFormSubmit}>{formData.id ? t.save : t.add}</button>
              <button onClick={() => setShowFormModal(false)}>{t.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps modal */}
      {mapModalOpen && (
        <div className="modal" role="dialog" aria-modal="true" onClick={() => setMapModalOpen(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 900, width: '95%', height: '70vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h4 style={{ margin: 0 }}>{t.address || 'Cím'}</h4>
              <button className="close-icon" onClick={() => setMapModalOpen(false)} aria-label="Bezárás">×</button>
            </div>

            <div style={{ margin: '8px 0 12px' }}>{mapAddress}</div>

            <iframe
              title="Google Maps"
              width="100%"
              height="100%"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&hl=${encodeURIComponent(language)}&z=15&output=embed`}
              loading="lazy"
              style={{ border: 0, borderRadius: 8, width: '100%', height: 'calc(100% - 60px)' }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.open_in_maps || 'Megnyitás a Google Térképen'}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserServices;